import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ProductListParams } from "../../ProductListURLConfig";
import { ProductListAction } from "../../ProductList";
import { RootState, store } from "../../../../utils/store";
import { useGetCustomerQuery } from "../../../../api/customerApi";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../../../../api/djangoToastSlice";
import {
  useGetViewSetsQuery,
  useCreateViewSetMutation,
  useCreateViewMutation,
  useUpdateViewMutation,
} from "../../../../api/viewSetApi";
import { hasActiveFilters } from "../../utils";
import { View, ViewSet, ViewSetId, ViewId } from "../types";

export type UseViewSetsParams = {
  currentParameters: ProductListParams;
  onProductListAction: (
    action: ProductListAction,
    value: ProductListParams
  ) => void;
  viewId: ViewId;
};

export type UseViewSetsResult = {
  activeViewId?: ViewId;
  activeViewSetId: number;
  haveActiveViewFiltersChanged: boolean;
  isViewSetsLoading: boolean;
  onActiveViewChange: (view?: View) => void;
  onViewCreate: (displayName: string, viewSetId: ViewSetId) => Promise<void>;
  onViewSelected: (view: View) => void;
  onViewSetCreate: (displayName: string) => Promise<void>;
  onViewUpdate: (viewId: ViewId) => Promise<void>;
  showCustomQueryHint: boolean;
  showViewSetsFilterButtons: boolean;
  toggleActiveViewSetAccordion: (viewSetId: number) => void;
  viewSetDropdownLabel: string;
  viewSets: ViewSet[] | undefined;
};

export const useViewSets = ({
  currentParameters,
  onProductListAction,
  viewId,
}: UseViewSetsParams): UseViewSetsResult => {
  const [updateView] = useUpdateViewMutation();
  const [createView] = useCreateViewMutation();
  const [createViewSet] = useCreateViewSetMutation();
  const {
    data: viewSets,
    isLoading: isFetchingViewSets,
  } = useGetViewSetsQuery();
  const {
    data: customer,
    isLoading: isCustomerLoading,
  } = useGetCustomerQuery();
  const [activeView, setActiveView] = useState<View | null>(null);
  const [activeViewSetId, setActiveViewSetId] = useState<number>(0);
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const customQuery = useSelector(
    (state: RootState) => state.productList.customQuery
  );

  useEffect(() => {
    /* When no filters are selected there's only 4 keys in currentParameters
     * This is to enable other components to deselect viewsets.
     * ObjectPagination needs to be able to reset viewsets if the ProductList fails */
    if (Object.keys(currentParameters).length === 4) {
      setActiveView(null);
      setSelectedView(null);
      setActiveViewSetId(0);
    }
  }, [currentParameters]);

  useEffect(() => {
    if (isFetchingViewSets || !viewSets) return;
    let currentActiveView: View | null = viewId ? null : activeView;
    let currentActiveViewSetId: number = 0;
    if (viewId) {
      for (const viewSet of viewSets) {
        const matchedView = viewSet.views.find(
          (view: { id: number }) => view.id === viewId
        );
        if (matchedView) {
          currentActiveView = matchedView;
          currentActiveViewSetId =
            viewSets.find((vs) => vs.id === matchedView.pviewset)?.id ?? 0;
          break;
        }
      }
    } else if (activeView) {
      currentActiveView = activeView;
      currentActiveViewSetId =
        viewSets.find((vs) => vs.id === activeView.pviewset)?.id ?? 0;
    }
    setActiveViewSetId(currentActiveViewSetId);
    setActiveView(currentActiveView);
    setSelectedView(currentActiveView);
  }, [isFetchingViewSets, viewSets, viewId]);

  const onActiveViewChange = (view?: View): void => {
    const targetView = view ?? selectedView;
    if (!targetView) return;
    onProductListAction(ProductListAction.UseViewSet, targetView.parameters);
    setActiveView(targetView);
  };

  const onViewSelected = (view: View): void => {
    setSelectedView(view);
    onProductListAction(ProductListAction.UseViewSet, view.parameters);
    setActiveView(view);
  };

  const toggleDjangoToast = (content: string): void => {
    store.dispatch(
      setDjangoToastOpen({
        appearance: NotificationAppearance.SUCCESS,
        content: content,
      })
    );
  };

  const onViewSetCreate = async (displayName: string): Promise<void> => {
    await createViewSet({ display_name: displayName });
    toggleDjangoToast("Filter group created successfully");
  };

  const onViewCreate = async (
    displayName: string,
    viewSetId: ViewSetId
  ): Promise<void> => {
    const newView = await createView({
      display_name: displayName,
      parameters: currentParameters,
      viewset: viewSetId,
    }).unwrap();
    onActiveViewChange(newView);
    setSelectedView(newView);
    setActiveViewSetId(viewSetId);
    toggleDjangoToast("Filter created successfully");
  };

  const onViewUpdate = async (viewId: ViewId): Promise<void> => {
    const updatedView = await updateView({
      view: viewId,
      parameters: currentParameters,
    }).unwrap();
    onActiveViewChange(updatedView);
    setSelectedView(updatedView);
    toggleDjangoToast("View updated successfully");
  };

  const showViewSetsFilterButtons = useMemo(() => {
    if (!customer) return false;
    return hasActiveFilters(customer, currentParameters);
  }, [currentParameters, customer]);

  const haveActiveViewFiltersChanged = useMemo(() => {
    const parameters = selectedView?.parameters || null;
    if (!parameters || !customer) return false;
    return hasActiveFilters(customer, currentParameters, parameters);
  }, [currentParameters, selectedView, customer]);

  const toggleActiveViewSetAccordion = (viewSetId: number): void => {
    const newViewSetId = activeViewSetId === viewSetId ? -1 : viewSetId;
    setActiveViewSetId(newViewSetId);
  };

  let viewSetDropdownLabel = "Default";
  if (activeView != null && viewSets) {
    const activeViewSet = viewSets.find(
      (viewSet: ViewSet) => viewSet.id === activeView.pviewset
    );
    viewSetDropdownLabel = activeViewSet
      ? `${activeViewSet.display_name} : ${activeView.display_name}`
      : activeView.display_name;
  }

  const isViewSetsLoading = !viewSets || isCustomerLoading;

  return {
    activeViewId: activeView?.id,
    activeViewSetId,
    haveActiveViewFiltersChanged,
    isViewSetsLoading,
    onActiveViewChange,
    onViewCreate,
    onViewSelected,
    onViewSetCreate,
    onViewUpdate,
    showCustomQueryHint: !customQuery?.query,
    showViewSetsFilterButtons,
    toggleActiveViewSetAccordion,
    viewSetDropdownLabel,
    viewSets,
  };
};
